<template>
  <div>
      <div style="display: inline-block; width: 170px">Subsections</div>
        <div class="border-container">

            <div ref="subsectionsDiv" class="row align-items-center" style="width: 300px; padding-left: 20px; padding-right: 20px">
              <template v-for="row in getSubsectionRows()">
              <div
                  v-for="child in row.children"
                  @click="selectSubsection(row, child)"
                  :key="child.id"
                  draggable="true"
                  :class="{ 'col subsection-block': !child.hidden, 'col block-hidden': child.hidden,
                   'selected': selectedSubsectionId === child.id}"
                  :style="{ flexGrow: child['flex-grow'] }">
              </div>
                <div class="break-row"></div>
              </template>
            </div>
          <div class="icon-container">

            <IconPlus title="Create New" class="action" @click="addSubsection"></IconPlus>
            <IconCopy title="Duplicate" class="action" @click="copySubsection"></IconCopy>
            <IconAddSubset title="New Line" class="action" @click="createSubsectionOnNewLine"></IconAddSubset>

            <IconPreviewClose title="Show" v-if="isHiddenSubsection" class="action" @click="showSubsection" style="margin-right: 15px"></IconPreviewClose>
            <IconPreviewOpen  title="Hide" v-else class="action" @click="hideSubsection" style="margin-right: 10px"></IconPreviewOpen>

            <IconDelete title="Delete" class="action" @click="deleteSubsection" style="margin-right: 15px"></IconDelete>
          </div>
        </div>
  </div>
</template>

<script setup lang="ts">
import Sortable from 'sortablejs/Sortable'
import {storeToRefs} from 'pinia'
import {useMainStore, useSlidesStore, useWidgetStore} from "@/store";
import {PPTDragDropElement, PPTHtmlElement} from "@/types/slides";
import useMicroservice from "@/hooks/useMicroservice";
import {computed, onMounted, PropType, ref} from "vue";

import {nanoid} from "nanoid";
import useHistorySnapshot from "@/hooks/useHistorySnapshot";
import useDeleteElement from "@/hooks/useDeleteElement";
import {OperateResizeHandlers} from "@/types/edit";

const slidesStore = useSlidesStore()
const widgetStore = useWidgetStore()
const {
  activeId,
  selectedDragDropId,
  selectedSubsectionId,
  selectedSubsectionRowId,
  selectedWidgets,
  subsectionSelected
} = storeToRefs(widgetStore)

const { updated } = storeToRefs(useMainStore())
const {addHistorySnapshot} = useHistorySnapshot()
const { deleteElement } = useDeleteElement()

const props = defineProps({
  onDeleteRow: {
    type: Function,
    required: true
  }
})

const subsectionsDiv = ref()
onMounted(() => {
  Sortable.create(subsectionsDiv.value, {
    animation: 150,
    onEnd: function (e) {

      const preIndex = e.oldIndex
      const newIndex = e.newIndex

      const rows = getSubsectionRows()

      // build out the conversion matrix
      const indices = []
      let count = 0

      for (let i = 0; i < rows.length; i++) {
        for (let j = 0; j < rows[i].children.length; j++) {
          indices[count++] = {
            row: i,
            col: j
          }
        }

        // end of row filler div
        indices[count++] = {
          row: i,
          col: rows[i].children.length,
          endOfRow: true
        }
      }

      const insertAfter = newIndex > preIndex
      const beforeIndex = indices[preIndex]

      // inserting after == inserting before the following node
      const afterIndex = indices[insertAfter ? newIndex + 1 : newIndex]
      const subsection = rows[beforeIndex.row].children[beforeIndex.col]

      rows[afterIndex.row].children.splice(afterIndex.col, 0, subsection)


      if (afterIndex.row === beforeIndex.row && afterIndex.col < beforeIndex.col) {
        // if we did an insert earlier in the row, the index is now +1 for the remove
        rows[beforeIndex.row].children.splice(beforeIndex.col+1, 1)
      }
      else {
        rows[beforeIndex.row].children.splice(beforeIndex.col, 1)
      }

      console.log('end', e)
      updated.value = Date.now()
    },
  });
})

const selectSubsection = (row, column) => {

  console.log('setting subsection id', column.id)
  selectedSubsectionRowId.value = row.id
  selectedSubsectionId.value = column.id

  subsectionSelected.value = true
  activeId.value = ''
  selectedWidgets.value =  []

  updated.value = Date.now()
}

const api = useMicroservice()

const save = () => {
  addHistorySnapshot()
  api.savePresentation()
  updated.value = Date.now()
}

const deleteSubsection = () => {

  let count = 0
  for (const row of getSubsectionRows()) {
    count += row.children.length
  }

  // we can just delete the whole section if there is one or zero sections
  if (count <= 1) {
    props.onDeleteRow()
  }
  else {
    const { row, dragdrop } = getSelectedSubsectionRow()

    for (const widget of getWidgetForSubsection(dragdrop, selectedSubsectionId.value, true)) {
      dragdrop.widgets.splice(dragdrop.widgets.indexOf(widget), 1);
    }

    const index = row.children.findIndex(child => child.id === selectedSubsectionId.value)
    row.children.splice(index, 1)

    selectedSubsectionId.value = index > 0 ? row.children[index - 1].id : row.children[index].id
    activeId.value = ''

    removeEmptyRows()
  }

  save()
}

const createSubsectionOnNewLine = () => {
  const { dragdrop } = getSelectedSubsectionRow()

  const newRow = createNewSubsectionRow(dragdrop)
  const newId = nanoid(10)
  newRow.children.push({
    id: newId,
    style: {
      margin: 5,
      valign: 'center',
      justifyContent: 'center',
      align: 'center'
    }
  })

  selectedSubsectionId.value = newId
  activeId.value = ''
  removeEmptyRows()
  updated.value = Date.now()
  save()
}

const removeEmptyRows = () => {
  const rows = getSubsectionRows()

  // go backwards so we can remove without affecting the index
  for (let i = rows.length-1; i >= 0; i--) {
    if (rows[i].children.length === 0) {
      rows.splice(i, 1)
    }
  }
}

const getWidgetForSubsection = (dragDrop, subsectionId, empty = false) => {
  const results: any[] = []

  for (const widget of dragDrop.widgets) {
    if (widget.parent.indexOf(dragDrop.id + '-' + subsectionId) === 0) {
      if (empty || widget.children.length > 0) {
        results.push(widget)
      }
    }
  }

  return results
}

const copySubsection = () => {
  const selectedSubsection = getSelectedSubsectionRow()
  const subsectionRow = selectedSubsection.row
  const index = subsectionRow.children.findIndex(child => child.id === selectedSubsectionId.value)

  const elIdMap = []
  const oldId = selectedSubsectionId.value
  const newId =  nanoid(10)

  elIdMap[oldId] = newId

  const clonedSubsection = JSON.parse(JSON.stringify(subsectionRow.children[index]))
  clonedSubsection.id = newId
  subsectionRow.children.splice(index + 1, 0, clonedSubsection)


  const dragDrop = selectedSubsection.dragdrop
  const clonedWidgets = []

  for (const widget of getWidgetForSubsection(dragDrop, oldId)) {
    const widgetClone = JSON.parse(JSON.stringify(widget))

    const first = widgetClone.parent.substring(0, 10)
    const second = widgetClone.parent.substring(11, 21)

    // staying in the same row but cloned column
    widgetClone.parent = first + '-' + elIdMap[second]

    for (const child of widgetClone.children) {
      child.id = nanoid(10)
    }

    clonedWidgets.push(widgetClone)
    updated.value = Date.now()
  }

  console.log('cloned', clonedWidgets)

  if (clonedWidgets.length === 0) {
    console.log('no cloned widgets')
  }

  dragDrop.widgets.push(...clonedWidgets)

  console.log('done', dragDrop.widgets)
  updated.value = Date.now()

  save()
  selectedSubsectionId.value = newId
  activeId.value = ''
  selectedWidgets.value = []
}

const showSubsection = () => {
  const { row } = getSelectedSubsectionRow()
  const index = row.children.findIndex(child => child.id === selectedSubsectionId.value)
  delete row.children[index].hidden
  save()
}

const isHiddenSubsection = computed(() => {
  const { row } = getSelectedSubsectionRow()
  const index = row.children.findIndex(child => child.id === selectedSubsectionId.value)
  return row.children[index]?.hidden
})

const hideSubsection = () => {
  const { row } = getSelectedSubsectionRow()
  const index = row.children.findIndex(child => child.id === selectedSubsectionId.value)
  row.children[index].hidden = true
  save()
}

const addSubsection = () => {
  const { row } = getSelectedSubsectionRow()
  const index = row.children.findIndex(child => child.id === selectedSubsectionId.value)


  const newId = nanoid(10)
  row.children.splice(index + 1, 0, {
    id: newId,
    style: {
      margin: 5,
      valign: 'center',
      justifyContent: 'center',
      align: 'center'
    }
  })

  selectedSubsectionId.value = newId
  activeId.value = ''
  updated.value = Date.now()
  save()
}

const canDelete = computed(() => {
  let count = 0
  for (const row of getSubsectionRows()) {
    count += row.children.length
  }
  return count > 1
})

const getSubsectionRows = () => {
  const dragDrop = getDragDrop()
  return dragDrop ? dragDrop.layout : []
}

const createNewSubsectionRow = (dragdrop) => {

  const rowJson = {
    'type': 'row',
    'children': []
  }

  const _row = JSON.parse(JSON.stringify(rowJson))
  _row.id = nanoid(10)

  if (dragdrop) {
    const layout = dragdrop.layout

    const index = layout.findIndex((row) => row.id === selectedSubsectionRowId.value)
    layout.splice(index + 1, 0, _row)

    slidesStore.updateElement({id: dragdrop.id, props: {layout: layout}})
    selectedSubsectionRowId.value = _row.id

    return _row
  }

  return { row: { children: [] } }
}

const getDragDrop = (): PPTDragDropElement | undefined => {
  if (selectedDragDropId.value) {
    for (const dragdrop of slidesStore.getElementsByType('dragdrop')) {
      if (dragdrop.id === selectedDragDropId.value) {
        return <PPTDragDropElement>dragdrop
      }
    }
  }
}

const getSelectedSubsectionRow = () => {
  const dragdrop = getDragDrop()
  if (dragdrop) {
    for (const child of dragdrop.layout) {
      if (child.id === selectedSubsectionRowId.value) {
        return {
          dragdrop: dragdrop,
          row: child
        }
      }
    }
  }

  return { row: { children: [] } }
}
</script>

<style lang="scss" scoped>

.border-container {
  margin-top: 15px;
  width: 310px;
  border: solid 1px #ccc;
  padding: 10px;
}

:deep(.flex) {
  display: flex;
  cursor: pointer;
}

:deep(ul) {
  list-style-type: none;
}

:deep(.group) {
  margin-top: 10px;
  display: flex;
  align-items: center;
  column-gap: 10px;
}

.subsection-block {
  background: orange;
  border: 2px solid orange;
  font-size: 11px;
  text-align: center;
  text-indent: -9999em;
  padding: 2px 0;
  -webkit-transition: background .2s ease;
  transition: background .2s ease;
  border-radius: 3px;
  height: 20px;
  margin: 0 2px 3px;
}

.selected {
  border: solid 2px #2196F3 !important;
}

.right-clickable {
  border: 2px solid transparent;
  padding: 5px;
  transition: all 0.3s ease;
}

.right-clickable:hover {
  border-color: #d1d1d1;
}


.block-hidden {
  background: grey;
  border: 2px solid grey;
  font-size: 11px;
  text-align: center;
  text-indent: -9999em;
  padding: 2px 0;
  -webkit-transition: background .2s ease;
  transition: background .2s ease;
  border-radius: 3px;
  height: 20px;
  margin: 0 2px 3px;
}

.action {
  font-size: 17px;
  border: 1px solid #cccccc;
  border-radius: 5px;
  padding: 5px;
}

.icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30px;
  gap: 1px;
  margin-top: 10px;
  margin-bottom: 5px;
}

.inline-block {
  display: inline-block;
}

.break-row {
  flex-basis: 100%;
  height: 0;
}

:deep(.ghost) {
  width: 100px !important;
}

:deep(.sortable-chosen) {
  display: block !important;
  width: 100px !important;
}

:deep(.sortable-ghost) {
  background-color: #2196F3 !important;
}

:deep(.p-button) {
  background: #fff;
  border: 1px solid #ccc;
  color: black;
}
</style>
